import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import ServiceChargeModal from './components/ServiceChargeModal';
import ProcessingModal from './components/ProcessingModal';
import EmailReceiptModal from './components/EmailReceiptModal';
import ReadAloudButton from './components/ReadAloudButton';
import ServiceDashboardHeader from './components/ServiceDashboardHeader';

const API = process.env.REACT_APP_BACKEND_URL;

function BOCPPage({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check where user came from for navigation
  const fromLocation = searchParams.get('from');
  const returnPath = fromLocation === 'activity' ? '/user_dashboard' : '/homepage';
  const returnLabel = fromLocation === 'activity' ? 'Return to Dashboard' : 'Return to Home';
  
  // Dashboard metrics state
  const [reports, setReports] = useState([]);
  const [transactions, setTransactions] = useState([]);
  
  const [formData, setFormData] = useState({
    concept_title: '',
    genre: '',
    target_audience: '',
    length: '',
    sub_genre: '',
    setting: '',
    main_character: '',
    target_age: '',
    themes: '',
    concept_description: '',
    style: '',
    tone: '',
    inspiration: '',
    target_word_count: '',
    publishing_goal: 'Traditional Publishing',
    category: '',
    theme: '',
    content: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [processingProgress, setProcessingProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [progressSteps, setProgressSteps] = useState([
    { text: 'Analyzing manuscript', completed: false, current: false },
    { text: 'Generating outline', completed: false, current: false },
    { text: 'Formatting results', completed: false, current: false }
  ]);
  const [customGenre, setCustomGenre] = useState('');
  const [showCustomGenre, setShowCustomGenre] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [showEmailReceiptModal, setShowEmailReceiptModal] = useState(false);
  const [lastTransactionId, setLastTransactionId] = useState(null);
  
  // Load outline if report ID is in URL
  useEffect(() => {
    const reportId = searchParams.get('report');
    if (reportId) {
      loadOutline(reportId);
    }
  }, [searchParams]);
  
  // Fetch dashboard metrics
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch all BOCP results
        const reportsResponse = await axios.get(`${API}/api/bocp/results`, {
          withCredentials: true
        });
        setReports(reportsResponse.data || []);

        // Fetch transaction history
        const transactionsResponse = await axios.get(`${API}/api/payments/transactions`, {
          withCredentials: true
        });
        setTransactions(transactionsResponse.data || []);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
      }
    };

    if (user) {
      fetchDashboardData();
    }
  }, [user]);
  
  const loadOutline = async (reportId) => {
    try {
      const response = await axios.get(`${API}/api/bocp/results/${reportId}`, {
        withCredentials: true
      });
      
      const outlineData = response.data;
      
      // Update document title
      document.title = `${outlineData.concept_title} | BOCP Report`;
      setResult(outlineData);
    } catch (err) {
      console.error('Failed to load outline:', err);
      setError('Failed to load outline. Please try again.');
      navigate('/user_dashboard');
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Handle genre selection
    if (name === 'genre') {
      if (value === 'Other') {
        setShowCustomGenre(true);
      } else {
        setShowCustomGenre(false);
        setCustomGenre('');
      }
    }
  };
  
  const handleCustomGenreChange = (value) => {
    setCustomGenre(value);
    if (value) {
      setFormData(prev => ({ ...prev, genre: value }));
    } else {
      setFormData(prev => ({ ...prev, genre: 'Other' }));
      setShowCustomGenre(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.concept_title.trim() || !formData.genre.trim()) {
      setError('Please provide at least a book title and genre.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    const submissionData = {
      name: 'Book Outline and Chapter Planner',
      bookTitle: formData.concept_title || 'BOCP Generation'
    };
    
    try {
      // Check if user needs to pay BEFORE processing (only for À La Carte users)
      if (user?.role === 'a_la_carte') {
        // Show payment authorization modal BEFORE any processing or charging
        setPaymentInfo({
          amount: 15.00,  // Premium service
          currency: 'USD',
          description: 'Book Outline & Chapter Planner - AI-powered chapter-by-chapter breakdown',
          submission_data: submissionData,
          name: 'Book Outline and Chapter Planner',
          bookTitle: formData.concept_title || 'BOCP Generation'
        });
        setShowPaymentModal(true);
        setLoading(false);
        return; // Stop here - wait for user to confirm payment
      }
      
      // Process the generation (for subscribers, admins, beta testers who don't need payment confirmation)
      await processGeneration();
      
    } catch (err) {
      console.error('BOCP submission error:', err);
      setError(err.response?.data?.detail || err.message || 'An error occurred while generating your outline.');
      setLoading(false);
    }
  };
  
  const processGeneration = async () => {
    try {
      // Step 1: Analyzing
      setCurrentStep('Analyzing book concept and themes...');
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: false, current: true },
        { text: 'Generating outline', completed: false, current: false },
        { text: 'Formatting results', completed: false, current: false }
      ]);
      setProcessingProgress(10);
      
      // Transform frontend form data to match backend BOCPRequest model
      const requestData = {
        title: formData.concept_title || 'Untitled Book',
        author: user?.first_name && user?.last_name 
          ? `${user.first_name} ${user.last_name}` 
          : 'Anonymous Author',
        publisher: formData.publishing_goal || 'Self Publish',
        category: formData.genre || 'General Fiction',
        theme: formData.themes || formData.theme || 'General',
        content: formData.concept_description || 'No description provided',
        book_length: formData.length?.toLowerCase().includes('novella') ? 'short' 
          : formData.length?.toLowerCase().includes('novel') ? 'long' 
          : 'medium',
        target_audience: formData.target_audience || '',
        include_intro: false,
        narrative_style: formData.style || 'Conversational'
      };
      
      setProcessingProgress(25);
      
      // Step 2: Generating outline
      setCurrentStep('Generating detailed chapter outline...');
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: true, current: false },
        { text: 'Generating outline', completed: false, current: true },
        { text: 'Formatting results', completed: false, current: false }
      ]);
      setProcessingProgress(40);
      
      const response = await axios.post(`${API}/api/bocp/generate`, requestData, {
        withCredentials: true
      });
      
      // Step 3: Formatting
      setCurrentStep('Finalizing book outline...');
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: true, current: false },
        { text: 'Generating outline', completed: true, current: false },
        { text: 'Formatting results', completed: false, current: true }
      ]);
      setProcessingProgress(85);
      
      // Step 11: Display the fully developed result screen
      setResult(response.data);
      
      // Complete
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: true, current: false },
        { text: 'Generating outline', completed: true, current: false },
        { text: 'Formatting results', completed: true, current: false }
      ]);
      setProcessingProgress(100);
      setCurrentStep('Complete!');
      
      // Refresh dashboard stats after successful service completion
      window.dispatchEvent(new CustomEvent('refreshDashboardStats'));
      
      // Update URL without reloading the page
      const newUrl = new URL(window.location);
      newUrl.searchParams.set('report', response.data.id);
      window.history.pushState({}, '', newUrl);
      
      // Update document title
      document.title = `${response.data.concept_title || response.data.title} | BOCP Report`;
      
      // Step 8: After payment → "Send Transaction Receipt" modal
      // (Steps 7, 9, 10 handled by backend: charge payment, send emails, update tracking)
      if (response.data.transaction_id) {
        setLastTransactionId(response.data.transaction_id);
        setShowEmailReceiptModal(true);
      }
      
      toast.success('Book outline generated successfully!');
      
    } catch (err) {
      console.error('Generation error:', err);
      setError(err.response?.data?.detail || err.message || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };
  
  const handlePaymentSuccess = async (paymentData) => {
    setShowPaymentModal(false);
    setLoading(true);
    
    try {
      // IMPORTANT: User confirmed willingness to pay
      // BUT actual charge happens in backend AFTER successful generation
      // This ensures users are only charged for successfully completed services
      
      await processGeneration();
      
      // Note: Backend will create transaction record after successful completion
      // Transaction ID will be in the response from processGeneration
      
    } catch (error) {
      console.error('Post-payment generation error:', error);
      setError(error.response?.data?.detail || error.message || 'Generation failed. You have NOT been charged.');
      setLoading(false);
    }
  };
  
  const exportToPDF = () => {
    if (!result) {
      toast.error('No outline data to export');
      return;
    }
    
    try {
      const pdf = new jsPDF();
      
      // Set up fonts and spacing
      const pageWidth = 190; // PDF width minus margins
      const leftMargin = 10;
      let yPosition = 20;
      
      // Header
      pdf.setFontSize(20);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(30, 64, 175); // Blue color
      
      const title = result.concept_title;
      pdf.text(title, leftMargin, yPosition);
      yPosition += 10;
      
      // Subtitle
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text('Book Outline & Chapter Plan', leftMargin, yPosition);
      yPosition += 15;
      
      // Book Overview
      if (result.book_overview) {
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(30, 64, 175);
        pdf.text('Book Overview', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(0, 0, 0);
        
        const overviewLines = pdf.splitTextToSize(result.book_overview, pageWidth);
        overviewLines.forEach(line => {
          if (yPosition > 275) {
            pdf.addPage();
            yPosition = 20;
          }
          pdf.text(line, leftMargin, yPosition);
          yPosition += 5;
        });
        yPosition += 10;
      }
      
      // Character Profiles
      if (result.character_profiles) {
        if (yPosition > 220) {
          pdf.addPage();
          yPosition = 20;
        }
        
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(30, 64, 175);
        pdf.text('Character Profiles', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(0, 0, 0);
        
        const characterLines = pdf.splitTextToSize(result.character_profiles, pageWidth);
        characterLines.forEach(line => {
          if (yPosition > 275) {
            pdf.addPage();
            yPosition = 20;
          }
          pdf.text(line, leftMargin, yPosition);
          yPosition += 5;
        });
        yPosition += 10;
      }
      
      // Chapter Outlines
      if (result.chapter_outlines) {
        if (yPosition > 220) {
          pdf.addPage();
          yPosition = 20;
        }
        
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(30, 64, 175);
        pdf.text('Chapter Outlines', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(0, 0, 0);
        
        const chapterLines = pdf.splitTextToSize(result.chapter_outlines, pageWidth);
        chapterLines.forEach(line => {
          if (yPosition > 275) {
            pdf.addPage();
            yPosition = 20;
          }
          pdf.text(line, leftMargin, yPosition);
          yPosition += 5;
        });
        yPosition += 10;
      }
      
      // Writing Guidelines
      if (result.writing_guidelines) {
        if (yPosition > 220) {
          pdf.addPage();
          yPosition = 20;
        }
        
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(30, 64, 175);
        pdf.text('Writing Guidelines', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(0, 0, 0);
        
        const guidelineLines = pdf.splitTextToSize(result.writing_guidelines, pageWidth);
        guidelineLines.forEach(line => {
          if (yPosition > 275) {
            pdf.addPage();
            yPosition = 20;
          }
          pdf.text(line, leftMargin, yPosition);
          yPosition += 5;
        });
      }
      
      // Footer on all pages
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Page ${i} of ${pageCount}`, 105, 287, { align: 'center' });
        pdf.text('Generated by Digital Ink AI - www.DigitalInkAI.com', 105, 292, { align: 'center' });
      }
      
      const filename = `BOCP_${result.concept_title.replace(/[^a-z0-9]/gi, '_')}_${new Date().getTime()}.pdf`;
      pdf.save(filename);
      
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    }
  };
  
  const handleNewOutline = () => {
    setResult(null);
    setError('');
    setFormData({
      concept_title: '',
      genre: '',
      target_audience: '',
      length: '',
      sub_genre: '',
      setting: '',
      main_character: '',
      target_age: '',
      themes: '',
      concept_description: '',
      style: '',
      tone: '',
      inspiration: '',
      target_word_count: '',
      publishing_goal: 'Traditional Publishing',
      category: '',
      theme: '',
      content: ''
    });
    
    // Clear URL parameters
    const newUrl = new URL(window.location);
    newUrl.searchParams.delete('report');
    window.history.pushState({}, '', newUrl);
    
    // Reset document title
    document.title = 'Book Outline & Chapter Planner | Digital Ink AI';
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-slate-100">
      {/* Header with Logo and Navigation */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/Digital_Ink_AI_Logo.png" 
                alt="Digital Ink AI Logo" 
                className="h-12 w-auto"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png";
                }}
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/homepage')}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                🏠 Home
              </button>
              <button
                onClick={() => navigate('/user_dashboard')}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                📊 Dashboard
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Service Title and Description */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            📚 Book Outline & Chapter Planner
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Transform book concepts into structured roadmaps. AI analyzes genre, themes, and audience to create comprehensive chapter-by-chapter breakdowns with character development arcs and writing guidelines. Keep projects organized from first draft to final manuscript with professional outlining that ensures narrative coherence and pacing throughout.
          </p>
        </div>

        {/* Pricing Info */}
        {user && (
          <div className="mb-8 bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-lg p-6">
            <div className="text-center">
              {user.role === 'subscriber' || user.role === 'admin' || user.role === 'beta_tester' ? (
                <div>
                  <h3 className="text-xl font-bold text-green-900 mb-2">✨ Unlimited Free Access</h3>
                  <p className="text-green-700">Included with your subscription - create as many outlines as you need!</p>
                </div>
              ) : (
                <div>
                  <h3 className="text-xl font-bold text-purple-900 mb-2">⭐ Premium Service - $15.00</h3>
                  <p className="text-blue-700">Complete book outline package charged to your payment method on file</p>
                </div>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-300 text-red-700 px-6 py-4 rounded-lg mb-6 print:hidden">
            {error}
          </div>
        )}

        {!result ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 print:hidden">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-lg border border-slate-200">
                <div className="p-6 border-b border-slate-200">
                  <div className="text-center mb-4">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" 
                      alt="Digital Ink AI" 
                      className="h-10 mx-auto mb-3"
                    />
                    <h2 className="text-2xl font-bold text-slate-900">📖 Book Concept Details</h2>
                    <p className="text-sm text-slate-600 mt-1">Powered by Digital Ink AI</p>
                  </div>
                </div>

                <div className="p-6">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Book Title/Concept *</label>
                      <input
                        type="text"
                        name="concept_title"
                        value={formData.concept_title}
                        onChange={handleInputChange}
                        placeholder="e.g., The Last Digital Nomad"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Genre *</label>
                        <select
                          name="genre"
                          value={formData.genre}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                          required
                        >
                          <option value="">Select Genre...</option>
                          <option value="Literary Fiction">Literary Fiction</option>
                          <option value="Science Fiction">Science Fiction</option>
                          <option value="Fantasy">Fantasy</option>
                          <option value="Romance">Romance</option>
                          <option value="Mystery/Thriller">Mystery/Thriller</option>
                          <option value="Horror">Horror</option>
                          <option value="Historical Fiction">Historical Fiction</option>
                          <option value="Young Adult">Young Adult</option>
                          <option value="Children's">Children's</option>
                          <option value="Non-Fiction">Non-Fiction</option>
                          <option value="Biography/Memoir">Biography/Memoir</option>
                          <option value="Self-Help">Self-Help</option>
                          <option value="Business">Business</option>
                          <option value="Other">Other</option>
                        </select>
                        
                        {showCustomGenre && (
                          <input
                            type="text"
                            placeholder="Please specify genre..."
                            value={customGenre}
                            onChange={(e) => handleCustomGenreChange(e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 mt-2"
                          />
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Target Length</label>
                        <select
                          name="length"
                          value={formData.length}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="">Select Length...</option>
                          <option value="Short Story (under 10,000 words)">Short Story (under 10,000 words)</option>
                          <option value="Novella (10,000-40,000 words)">Novella (10,000-40,000 words)</option>
                          <option value="Novel (40,000-80,000 words)">Novel (40,000-80,000 words)</option>
                          <option value="Epic Novel (80,000+ words)">Epic Novel (80,000+ words)</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Target Audience</label>
                      <input
                        type="text"
                        name="target_audience"
                        value={formData.target_audience}
                        onChange={handleInputChange}
                        placeholder="e.g., Adults 25-45, Tech professionals, Fantasy readers"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Main Themes</label>
                      <input
                        type="text"
                        name="themes"
                        value={formData.themes}
                        onChange={handleInputChange}
                        placeholder="e.g., Identity, Technology, Human connection, Power"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Concept Description</label>
                      <textarea
                        name="concept_description"
                        value={formData.concept_description}
                        onChange={handleInputChange}
                        rows={6}
                        placeholder="Describe your book concept, main plot points, characters, setting..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Writing Style</label>
                        <select
                          name="style"
                          value={formData.style}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="">Select Style...</option>
                          <option value="First Person">First Person</option>
                          <option value="Third Person Limited">Third Person Limited</option>
                          <option value="Third Person Omniscient">Third Person Omniscient</option>
                          <option value="Multiple POV">Multiple POV</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Tone</label>
                        <select
                          name="tone"
                          value={formData.tone}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="">Select Tone...</option>
                          <option value="Serious">Serious</option>
                          <option value="Humorous">Humorous</option>
                          <option value="Dark">Dark</option>
                          <option value="Optimistic">Optimistic</option>
                          <option value="Suspenseful">Suspenseful</option>
                          <option value="Emotional">Emotional</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Setting</label>
                      <input
                        type="text"
                        name="setting"
                        value={formData.setting}
                        onChange={handleInputChange}
                        placeholder="e.g., Near-future Tokyo, Medieval England, Small town America"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Main Character Description</label>
                      <textarea
                        name="main_character"
                        value={formData.main_character}
                        onChange={handleInputChange}
                        rows={4}
                        placeholder="Describe your protagonist: background, motivation, personality, goals..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Publishing Goal</label>
                      <select
                        name="publishing_goal"
                        value={formData.publishing_goal}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="Traditional Publishing">Traditional Publishing</option>
                        <option value="Self Publishing">Self Publishing</option>
                        <option value="Hybrid Publishing">Hybrid Publishing</option>
                        <option value="Personal/Portfolio">Personal/Portfolio</option>
                      </select>
                    </div>

                    <div className="pt-6 border-t border-gray-200">
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 disabled:bg-gray-400 text-white font-bold py-4 px-6 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Generating Outline...' : 'Generate Book Outline & Chapters with Digital Ink AI'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Generation Features */}
              <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">🎯 Generation Features</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start">
                    <span className="text-indigo-500 mr-2">✓</span>
                    <span>Chapter-by-chapter breakdowns</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-indigo-500 mr-2">✓</span>
                    <span>Character development arcs</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-indigo-500 mr-2">✓</span>
                    <span>Plot progression framework</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-indigo-500 mr-2">✓</span>
                    <span>Scene-by-scene descriptions</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-indigo-500 mr-2">✓</span>
                    <span>Marketing hooks per chapter</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-indigo-500 mr-2">✓</span>
                    <span>Writing guidelines and tips</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Results Display */
          <div className="space-y-8">
            {/* Header Controls */}
            <div className="flex justify-between items-center bg-white rounded-lg p-6 shadow-lg border">
              <div>
                <h1 className="text-3xl font-bold text-slate-900">{result.outline_title || result.title}</h1>
                <p className="text-lg text-slate-600">{result.category}</p>
              </div>
              <div className="flex gap-3">
                <Button onClick={() => window.print()} variant="outline" className="flex items-center gap-2">
                  🖨️ Print
                </Button>
                <Button onClick={exportToPDF} variant="outline" className="flex items-center gap-2">
                  📄 Export PDF
                </Button>
                <Button onClick={handleNewOutline} className="bg-indigo-600 hover:bg-indigo-700">
                  📚 New Outline
                </Button>
              </div>
            </div>

            {/* Book Overview */}
            {result.introduction_summary && (
              <Card className="bg-white shadow-lg border border-slate-200">
                <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50 border-b">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-2xl text-indigo-900">📖 Introduction</CardTitle>
                    <ReadAloudButton text={result.introduction_summary} serviceType="bocp" />
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="prose max-w-none text-slate-700 leading-relaxed">
                    {result.introduction_summary.split('\n\n').map((paragraph, index) => (
                      <p key={index} className="mb-4 last:mb-0">{paragraph}</p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {result.chapters && result.chapters.length > 0 && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-indigo-900">📚 Chapter Outlines ({result.chapters.length} Chapters)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {result.chapters.map((chapter, index) => (
                      <div key={index} className="border-l-4 border-indigo-500 pl-4 py-2">
                        <h3 className="text-xl font-bold text-indigo-900 mb-2">
                          Chapter {chapter.number}: {chapter.title}
                        </h3>
                        <div className="mb-3">
                          <h4 className="font-semibold text-slate-700 mb-1">Summary:</h4>
                          <p className="text-slate-600">{chapter.summary}</p>
                        </div>
                        {chapter.key_events && chapter.key_events.length > 0 && (
                          <div className="mb-3">
                            <h4 className="font-semibold text-slate-700 mb-1">Key Events:</h4>
                            <ul className="list-disc list-inside text-slate-600 space-y-1">
                              {chapter.key_events.map((event, eventIdx) => (
                                <li key={eventIdx}>{event}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {chapter.marketing_hook && (
                          <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                            <p className="text-sm font-semibold text-amber-900 mb-1">Marketing Hook:</p>
                            <p className="text-sm text-amber-800">{chapter.marketing_hook}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {result.conclusion_summary && (
              <Card className="mb-6">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-indigo-900">🏁 Conclusion</CardTitle>
                    <ReadAloudButton text={result.conclusion_summary} serviceType="bocp" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="prose max-w-none text-slate-700">
                    {result.conclusion_summary.split('\n\n').map((paragraph, index) => (
                      <p key={index} className="mb-4">{paragraph}</p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {result.marketing_notes && (
              <Card className="mb-6">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-indigo-900">📈 Marketing Notes</CardTitle>
                    <ReadAloudButton text={result.marketing_notes} serviceType="bocp" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="prose max-w-none text-slate-700">
                    {result.marketing_notes.split('\n\n').map((paragraph, index) => (
                      <p key={index} className="mb-4">{paragraph}</p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Modals */}
        {showPaymentModal && paymentInfo && (
          <ServiceChargeModal
            isOpen={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            onConfirm={handlePaymentSuccess}
            serviceInfo={paymentInfo}
            user={user}
          />
        )}

        {loading && (
          <ProcessingModal
            isOpen={loading}
            serviceName="Book Outline & Chapter Planner (BOCP)"
            bookTitle={formData.concept_title}
            progress={processingProgress}
            currentStep={currentStep}
            progressSteps={progressSteps}
          />
        )}

        {showEmailReceiptModal && lastTransactionId && (
          <EmailReceiptModal
            isOpen={showEmailReceiptModal}
            onClose={() => setShowEmailReceiptModal(false)}
            transactionId={lastTransactionId}
            userEmail={user?.email}
          />
        )}
      </main>
    </div>
  );
}

export default BOCPPage;